/**
 * World 3 — "Cinder Pass" (Fire, levels 21–30). Act I, chapter 3 of "The
 * Last Souls": the Drago Guardian that forgot its fire was ever a gift. Rival
 * levels and player caps are stamped from World 3's band (21→30) at module load
 * by campaignConfig.ts — squads here only author species, nicknames, and slot
 * order (slot 0 of the finale = the Guardian). Talion is Clan Z's alone, so Fire has two
 * species left (Hotpaw/Drago): every trainer line-up is Fire as a MAJORITY, with at most one
 * outsider — Pikoto — in the third slot (enforced in campaignService.test.ts). Identical
 * line-ups are what `campaign-gauntlet`'s N5 catches (it once found four Forge Heart nodes in a
 * row fielding the same trio), so variety comes from slot order, archetype, nickname, the WILD
 * encounter `z8-nw1`, and the 1v1→2v2→3v3 opponent ramp.
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  HOTPAW,
  TALION,
  DRAGO,
  PEFAREON,
  SUMIYA,
  PIKOTO,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import {
  WORLD3_NODE_STORIES as S,
  WORLD3_ZONE_STORIES as Z,
  WORLD3_TAGLINE,
} from './stories/world3.js';

export const WORLD_3: CampaignWorld = {
  worldId: 'w3',
  name: 'Cinder Pass',
  element: 'Fire',
  featuredSpeciesId: DRAGO,
  story: WORLD3_TAGLINE,
  locked: true,
  zones: [
    {
      zoneId: 'z6',
      name: 'Scorch Road',
      element: 'Fire',
      story: Z['z6'],
      nodes: [
        node({
          nodeId: 'z6-n1',
          kind: 'normal',
          rivalName: 'Timmy',
          rivalTitle: 'Youngster',
          ...S['z6-n1'],
          squad: [rival(HOTPAW, 'Toasty'), rival(PIKOTO, 'Sparky'), rival(DRAGO, 'Pyre')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: HOTPAW,
        }),
        giftNode({
          nodeId: 'z6-g1',
          giver: 'Ash Trader',
          label: 'Trailside Gift',
          story: 'A trader warming his hands by the coals nods at you.',
          line: 'Found these in the coals. Soul Shards, Keeper!',
          reward: { tracks: giftTracks(DRAGO) },
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z6-n2',
          kind: 'normal',
          rivalName: 'Mimi',
          rivalTitle: 'Lass',
          ...S['z6-n2'],
          squad: [rival(PIKOTO, 'Flick'), rival(HOTPAW, 'Ashling'), rival(DRAGO, 'Coal')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z6-n3',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Ashclaw',
          rivalTitle: 'Wild Suikamon',
          ...S['z6-n3'],
          squad: [rival(HOTPAW, 'Scorch'), rival(DRAGO, 'Smoulder'), rival(PIKOTO, 'Flare')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: HOTPAW,
        }),
        node({
          nodeId: 'z6-n4',
          kind: 'zone_boss',
          rivalName: 'Cliff',
          rivalTitle: 'Bird Keeper',
          ...S['z6-n4'],
          squad: [rival(PIKOTO, 'Gatefire'), rival(DRAGO, 'Bulwark'), rival(HOTPAW, 'Sear')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: PIKOTO,
        }),
      ],
    },
    {
      zoneId: 'z7',
      name: 'Ember Woods',
      element: 'Fire',
      story: Z['z7'],
      nodes: [
        node({
          nodeId: 'z7-n1',
          kind: 'normal',
          rivalName: 'Zip',
          rivalTitle: 'Clan Z',
          ...S['z7-n1'],
          squad: [rival(TALION, 'Dash'), rival(DRAGO, 'Glow'), rival(HOTPAW, 'Rake')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TALION,
        }),
        giftNode({
          nodeId: 'z7-g1',
          giver: 'Old Smith',
          label: 'Training Charm',
          story: 'A smith cooling a fresh-forged charm looks up as you pass.',
          line: 'Forged this to make young ones stronger. Yours.',
          reward: { xp: giftXp(2) },
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z7-n2',
          kind: 'normal',
          rivalName: 'Vera',
          rivalTitle: 'Channeler',
          ...S['z7-n2'],
          squad: [rival(DRAGO, 'Hammer'), rival(PIKOTO, 'Anvil'), rival(HOTPAW, 'Tongs')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z7-n3',
          kind: 'normal',
          rivalName: 'Timmy',
          rivalTitle: 'Youngster',
          ...S['z7-n3'],
          squad: [rival(PIKOTO, 'Plume'), rival(HOTPAW, 'Haze'), rival(DRAGO, 'Veil')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z7-n4',
          kind: 'zone_boss',
          rivalName: 'Aggie',
          rivalTitle: 'Medium',
          ...S['z7-n4'],
          squad: [rival(DRAGO, 'Witchfire'), rival(HOTPAW, 'Sigil'), rival(PIKOTO, 'Brand')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: DRAGO,
        }),
      ],
    },
    {
      zoneId: 'z8',
      name: 'Forge Heart',
      element: 'Fire',
      story: Z['z8'],
      nodes: [
        node({
          nodeId: 'z8-n1',
          kind: 'normal',
          rivalName: 'Zap',
          rivalTitle: 'Clan Z',
          ...S['z8-n1'],
          squad: [rival(PEFAREON, 'Shade'), rival(PIKOTO, 'Quench'), rival(HOTPAW, 'Bellow')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PEFAREON,
        }),
        giftNode({
          nodeId: 'z8-g1',
          giver: 'Shrine Keeper',
          label: 'Soul Token',
          story: 'In the dark, a cinder shrine flickers just ahead of you.',
          line: 'The forge leaves a big handful of Soul Shards here. Take them!',
          reward: { tracks: giftTracks(DRAGO, true), suika: GIFT_SKILL_SUIKA },
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z8-n2',
          kind: 'normal',
          rivalName: 'Lulu',
          rivalTitle: 'Lass',
          ...S['z8-n2'],
          squad: [rival(PIKOTO, 'Dousal'), rival(HOTPAW, 'Pail'), rival(DRAGO, 'Cistern')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z8-n3',
          kind: 'mini_boss',
          rivalName: 'Mimi',
          rivalTitle: 'Lass',
          ...S['z8-n3'],
          squad: [rival(DRAGO, 'Vigil'), rival(HOTPAW, 'Watchfire'), rival(PIKOTO, 'Ashwatch')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z8-nw1',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Cinderclaw',
          rivalTitle: 'Wild Suikamon',
          ...S['z8-nw1'],
          // The third slot goes to Pefareon — this is a WILD encounter, the one node in the
          // stretch the element rule exempts, so it is where N5's variety was available. The
          // Drago leads and the portrait follows the lead rather than the slot it used to sit in.
          squad: [rival(DRAGO, 'Kindle'), rival(HOTPAW, 'Ember'), rival(SUMIYA, 'Scald')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z8-n4',
          kind: 'zone_boss',
          rivalName: 'Cinder',
          rivalTitle: 'Forge Guardian',
          ...S['z8-n4'],
          squad: [rival(DRAGO, 'Hearth'), rival(PIKOTO, 'Cinderkin'), rival(HOTPAW, 'Emberkin')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: DRAGO,
          soulSpeciesId: DRAGO,
        }),
      ],
    },
  ],
};
